import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  isCurrentPlan,
  makePlanLongId,
  makePlanUrlPartForApi,
  useTranslate,
} from "../../helpers";
import { Plan, PlanDetailsPageSection, PrescriptionDrug } from "../../@types";
import { PlanDetailsLink, SuppressionHandler } from "../../components";
import { getOtherDrugInfo } from "../../api";
import routes from "../../app/routes";
import { AppContext } from "../../app/store";
import { useLocation } from "react-router";
import englishTranslations from "../../translations/en-US.json";
import {
  AnalyticsActionType,
  Ga4ParentComponentType,
} from "../../app/contexts/Analytics/types";

export const CoveredDrugs: FunctionComponent<{
  generalAlertMessage: JSX.Element | undefined;
  plan: Plan;
  prescriptions: PrescriptionDrug[];
  selectedPlan?: Plan;
  hasPrescriptions?: boolean;
}> = ({
  generalAlertMessage,
  hasPrescriptions = false,
  plan,
  prescriptions,
  selectedPlan,
}) => {
  const t = useTranslate();
  const { state, dispatch } = useContext(AppContext);
  const location = useLocation();
  const [coveredDrugs, setCoveredDrugs] = useState<number | undefined>(
    undefined
  );
  const isYearOverYear = location.pathname === routes.yearOverYear;
  const currentPlan = isYearOverYear
    ? isCurrentPlan(state.currentCoverage, plan)
    : isCurrentPlan(state.currentCoverage, plan) ||
      isCurrentPlan(state.nextYearCoverage, plan);

  const getCoveredDrugs = async (): Promise<void> => {
    if (hasPrescriptions) {
      const ndcs = prescriptions.map(p => p.ndc);

      const planUrl = makePlanUrlPartForApi(plan);
      try {
        const otherDrugInfo = await getOtherDrugInfo(ndcs, planUrl);

        const covered =
          otherDrugInfo?.reduce(
            (prev, curr) => prev + (curr.on_formulary ? 1 : 0),
            0
          ) ?? 0;

        setCoveredDrugs(covered);
      } catch (_e) {
        setCoveredDrugs(undefined);
      }
    }
  };

  useEffect(() => {
    getCoveredDrugs();
  }, []);

  return (
    <td
      className={`${
        selectedPlan?.id === plan.id ? "PlanComparePage__selectedCell" : ""
      }`}
    >
      {generalAlertMessage ? (
        generalAlertMessage
      ) : (
        <>
          <span className="ds-text-heading--md ds-u-display--block ds-u-lg-display--none mct-hide-print">
            {t("plan_terms.drugs_covered")}
          </span>
          <SuppressionHandler redactions={plan.redactions}>
            {coveredDrugs === undefined ? (
              <div className="ds-u-color--error">{t("error.drug_info")}</div>
            ) : (
              <>
                <div className="DrugCosts__label">{`${coveredDrugs} ${t(
                  "of"
                )} ${prescriptions.length}`}</div>
                <div>{t("plan_terms.prescription_drugs_covered")}</div>
                <PlanDetailsLink
                  className="mct-hide-print"
                  label={t("restrictions_may_apply")}
                  plan={plan}
                  onClick={() => {
                    dispatch({
                      type:
                        AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
                      settings: {
                        linkText: englishTranslations["restrictions_may_apply"],
                        linkUrl: `${routes.planDetails.replace(
                          ":id",
                          makePlanLongId(plan)
                        )}${location.search}#${
                          PlanDetailsPageSection.DRUG_COVERAGE
                        }`,
                        custom: {
                          parent_component_heading:
                            isYearOverYear && !currentPlan
                              ? "next year plan"
                              : "current year plan",
                          parent_component_type: Ga4ParentComponentType.TABLE,
                        },
                      },
                    });
                  }}
                  section={PlanDetailsPageSection.DRUG_COVERAGE}
                />
              </>
            )}
          </SuppressionHandler>
        </>
      )}
    </td>
  );
};
